<script>
  import { onMount, onDestroy } from "svelte";
  import { fade } from "svelte/transition";
  import Fa from "svelte-fa";
  import {
    faSpinner,
    faStop,
    faCopy,
    faStar,
    faRightFromBracket,
    faCircleQuestion,
    faCheck
  } from "@fortawesome/free-solid-svg-icons";
  import { watchAuthState, googleSignIn, googleSignOut } from "./firebase.js";
  import Navbar from "./components/Navbar.svelte";
  import AlertBar from "./components/AlertBar.svelte";
  import HowToUseOverlay from "./components/HowToUseOverlay.svelte";
  import SubscriptionModal from "./components/SubscriptionModal.svelte";
  import ManageSubscriptionModal from "./components/ManageSubscriptionModal.svelte";
  import Records from "./components/Records.svelte";
  import Footer from "./components/Footer.svelte";
  import Bookmark from "./components/Bookmark.svelte";

  // In production, both devMode and testing are false
  const devMode = false;
  const testing = false;
  let API_URL = testing
    ? "http://localhost:3000"
    : "https://aicloakerbackend-b665c292194b.herokuapp.com";

  // Basic user data and flags
  let user = null;
  let signedIn = false;
  let avatar = "";
  let gold = false;
  let platinum = false;
  let admin = false;

  // This boolean must be stored and retrieved globally (e.g. from a DB):
  // We will load it from /getGlobalSettings on mount
  let underDevelopment = false;

  // Tutorial controls
  let showTutorial = false;
  let currentStep = 0;
  let highlightBox = null;
  let tooltipStyle = `top: 50%; left: 50%; opacity: 0; transform: translate(-50%, -50%);`;

  // Steps for tutorial
  let steps = [
    {
      title: "Paste Your Text",
      text: "Place the text you want to cloak in the main input box. You can type or paste any text here.",
      selector: ".input-panel .text-area",
    },
    {
      title: "Select Cloak Style",
      text: "Use this dropdown to choose how your text will be rewritten (e.g. Generic, Professional, etc.).",
      selector: "#mode-select",
    },
    {
      title: "Rewrite",
      text: "Click the Rewrite button to transform your text. This may take a moment to process.",
      selector: "#rewrite-button",
    },
    {
      title: "Copy the Result",
      text: "Once rewriting is complete, press Copy to copy your new text. You're done!",
      selector: ".output-panel .btn-secondary",
    },
  ];

  function nextStep() {
    if (currentStep < steps.length - 1) {
      currentStep += 1;
      positionTooltip();
    } else {
      closeTutorial();
    }
  }

  function prevStep() {
    if (currentStep > 0) {
      currentStep -= 1;
      positionTooltip();
    }
  }

  function closeTutorial() {
    showTutorial = false;
  }

  // Position the tutorial highlight and tooltip
  async function positionTooltip() {
    const step = steps[currentStep];
    if (!step || !step.selector) return;

    const elem = document.querySelector(step.selector);
    if (!elem) {
      highlightBox = null;
      tooltipStyle = `top: 50%; left: 50%; opacity: 1; transform: translate(-50%, -50%);`;
      return;
    }

    // only scroll if we're NOT on the "Rewrite" step
    if (currentStep !== 2) {
      elem.scrollIntoView({ behavior: "smooth", block: "center" });
      await new Promise(r => setTimeout(r, 250));
    }

    const rect = elem.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    highlightBox = {
      top: rect.top + scrollTop,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };

    const tooltipX = rect.left + rect.width / 2;
    const tooltipY = rect.top + scrollTop + rect.height + 10;
    tooltipStyle = `
      top: ${tooltipY}px;
      left: ${tooltipX}px;
      opacity: 1;
      transform: translate(-50%, 0);
    `;
  }

  function handleBackgroundClick(e) {
    if (e.target.classList.contains("tutorial-background")) {
      closeTutorial();
    }
  }

  let showSubscriptionModal = false;
  let showManageSubscriptionModal = false;
  let loadingSubscription = false;
  let agreedToTos = false;

  let showProfileMenu = false;
  let userCredits = 0;
  let userId = "";
  let userEmail = "";
  let referredUsers = 0;
  let mode = "generic";
  let useStyle = true;
  let isGenerating = false;
  let abortController = null;
  let styleId = "";
  let styleFilename = "";

  let alertMessage = "";
  let alertVisible = false;
  let alertType = "error";

  let showHowToUse = false;
  function toggleHowToUse() {
    showHowToUse = !showHowToUse;
  }

  let inputText = "";
  let outputText = "";

  // Word & char counts
  $: wordCount = inputText
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  $: charCount = inputText.length;

  function showAlertBar(message, type = "error", duration = 4000) {
    alertMessage = message;
    alertType = type;
    alertVisible = true;
    setTimeout(() => {
      alertVisible = false;
    }, duration);
  }

  let showBookmarkPrompt = false;

  // Fetch global setting from DB
  async function getGlobalSettings() {
    try {
      const resp = await fetch(`${API_URL}/getGlobalSettings`);
      if (!resp.ok) throw new Error("Failed to fetch global settings.");
      const data = await resp.json();
      underDevelopment = data.underDevelopment; // store globally
    } catch (err) {
      console.error("Error fetching global settings:", err);
    }
  }

  // Update global setting in DB (only admin calls this)
  async function updateGlobalSettings(newValue) {
    try {
      const resp = await fetch(`${API_URL}/updateGlobalSettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ underDevelopment: newValue })
      });
      if (!resp.ok) {
        const r = await resp.json().catch(() => {});
        throw new Error(r?.message || "Failed to update global settings.");
      }
      // If successful, set local variable
      underDevelopment = newValue;
    } catch (err) {
      showAlertBar("Error updating underDevelopment: " + err.message, "error");
      console.error(err);
    }
  }

  onMount(() => {
    getGlobalSettings(); // load global "underDevelopment"

    if (devMode) {
      user = {
        uid: "localDevUser",
        email: "localdev@example.com",
        photoURL: "",
        gold: false,
        platinum: false,
        admin: false,
        referredUsers: 2,
        isNewUser: true
      };
      signedIn = true;
      avatar = user.photoURL || "profilepic.png";
      gold = user.gold;
      platinum = user.platinum;
      admin = user.admin;
      userId = user.uid;
      userEmail = user.email;
      userCredits = 3;
      referredUsers = user.referredUsers;
      if (user.isNewUser) {
        showBookmarkPrompt = true;
        showTutorial = true;
      }
    } else {
      watchAuthState(async (u) => {
        user = u;
        signedIn = !!u;
        if (signedIn) {
          avatar = u.profilepic || "profilepic.png";
          gold = u.gold || false;
          platinum = u.platinum || false;
          admin = u.admin || false;
          userId = u.uid || "";
          userEmail = u.email || "";
          userCredits = u.credits || 0;

          try {
            const updatedUserDoc = await getUserDoc(userId);
            referredUsers = updatedUserDoc.referredUsers || 0;
            gold = updatedUserDoc.gold || false;
            platinum = updatedUserDoc.platinum || false;

            if (updatedUserDoc.isNewUser) {
              showBookmarkPrompt = true;
              showTutorial = true;
            } else {
              showBookmarkPrompt = false;
              showTutorial = false;
            }
          } catch (err) {
            console.error("Error fetching user doc:", err);
          }
        } else {
          avatar = "";
          gold = false;
          platinum = false;
          admin = false;
          userId = "";
          userEmail = "";
          userCredits = 0;
          referredUsers = 0;
          showBookmarkPrompt = false;
          showTutorial = false;
        }
      });
    }

    document.addEventListener("click", handleClickOutside);
    confirmSubscriptionIfNeeded();

    if (showTutorial) {
      setTimeout(positionTooltip, 250);
    }
  });

  // Re-run positioning whenever showTutorial becomes true
  $: if (showTutorial) {
    setTimeout(positionTooltip, 250);
  }

  onDestroy(() => {
    document.removeEventListener("click", handleClickOutside);
  });

  function handleClickOutside(e) {
    if (!showProfileMenu) return;
    const menu = document.querySelector(".profile-menu");
    const avatarWrapper = document.querySelector(".avatar-wrapper");
    if (
      menu &&
      !menu.contains(e.target) &&
      avatarWrapper &&
      !avatarWrapper.contains(e.target)
    ) {
      showProfileMenu = false;
    }
  }

  async function getUserDoc(id) {
    const resp = await fetch(`${API_URL}/getUser/${id}`);
    if (!resp.ok) throw new Error("Could not fetch user doc");
    return resp.json();
  }

  async function toggleProfileMenu() {
    showProfileMenu = !showProfileMenu;
    if (showProfileMenu && userId) {
      try {
        const updatedUserDoc = await getUserDoc(userId);
        referredUsers = updatedUserDoc.referredUsers || 0;
        gold = updatedUserDoc.gold || false;
        platinum = updatedUserDoc.platinum || false;
      } catch (err) {
        console.error("Error fetching updated user doc:", err);
      }
    }
  }

  async function handleSignIn() {
    if (devMode) {
      showAlertBar("devMode ON — skipping sign-in", "info");
      return;
    }
    try {
      const newUser = await googleSignIn();
      const updatedDoc = await getUserDoc(newUser.uid);
      if (updatedDoc.isNewUser) {
        showBookmarkPrompt = true;
        showTutorial = true;
      } else {
        showBookmarkPrompt = false;
        showTutorial = false;
      }
      user = newUser;
      signedIn = !!newUser;
      avatar = updatedDoc.profilepic || "profilepic.png";
      gold = newUser.gold || false;
      platinum = newUser.platinum || false;
      admin = newUser.admin || false;
      userId = newUser.uid || "";
      userEmail = newUser.email || "";
      userCredits = newUser.credits || 0;
      referredUsers = newUser.referredUsers || 0;
    } catch (err) {
      showAlertBar("Sign in failed: " + err.message, "error");
    }
  }

  async function handleSignOut() {
    if (devMode) {
      showAlertBar("devMode ON — skipping sign-out", "info");
      return;
    }
    await googleSignOut();
    user = null;
    signedIn = false;
    avatar = "";
    gold = false;
    platinum = false;
    admin = false;
    userId = "";
    userEmail = "";
    userCredits = 0;
    showBookmarkPrompt = false;
    showTutorial = false;
  }

  function handleAvatarError(e) {
    e.target.src = "profilepic.png";
  }

  async function handleCancelGold() {
    if (!devMode && !signedIn) return;
    try {
      const resp = await fetch(`${API_URL}/cancelGold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (!resp.ok) throw new Error("Failed to cancel gold");
      const data = await resp.json();
      if (data.resultCode === 0) {
        gold = false;
        showAlertBar("Gold Cancelled Successfully", "success");
      } else {
        showAlertBar("Could not cancel gold, code = " + data.resultCode, "error");
      }
    } catch (err) {
      showAlertBar("Error canceling gold: " + err.message, "error");
    }
  }

  function clearText() {
    inputText = "";
    outputText = "";
  }

  function copyOutput() {
    if (!outputText) return;
    navigator.clipboard.writeText(outputText);
    showAlertBar("Copied to clipboard!", "info");
  }

  async function uploadStyle(e) {
    if (!devMode && (!user || !user.uid)) {
      showAlertBar("Please sign in first!", "error");
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    styleFilename = file.name;
    const formData = new FormData();
    formData.append("style_file", file);
    formData.append("userId", user.uid);

    try {
      const response = await fetch(`${API_URL}/upload-style`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Upload failed");
      }
      showAlertBar("Style uploaded successfully!", "success");
      styleId = data.styleDocId;
    } catch (err) {
      showAlertBar("Error uploading style: " + err.message, "error");
    }
  }

  function stopGeneration() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    isGenerating = false;
  }

  async function humanizeText() {
    if (!devMode && !signedIn) {
      showAlertBar("Please sign in first!", "error");
      return;
    }
    if (!inputText) {
      showAlertBar("Please enter text first!", "error");
      return;
    }
    if (wordCount < 50) {
      showAlertBar("We require a minimum of 50 words. Please add more content and try again.", "error");
      return;
    }
    if (!gold && !platinum && !admin && userCredits < 1) {
      showAlertBar("You need credits or a Gold/Platinum subscription.", "error");
      return;
    }
    if (!gold && !platinum && !admin) {
      const deductResp = await fetch(`${API_URL}/deductCredits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, amount: 1 }),
      });
      const deductData = await deductResp.json();
      if (deductData.resultCode === 1) {
        showAlertBar("You must purchase a gold subscription to use this feature.", "error");
        return;
      } else if (deductData.resultCode !== 0) {
        showAlertBar("Error deducting credits. Please try again later.", "error");
        return;
      } else {
        userCredits -= 1;
      }
    }
    outputText = "";
    isGenerating = true;
    abortController = new AbortController();

    try {
      outputText = "Rewriting...";
      const response = await fetch(`${API_URL}/humanize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: inputText,
          mode,
          useStyle,
          styleId,
          userId: user?.uid,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => {});
        throw new Error(errData?.error || "Server error");
      }

      const data = await response.json();
      outputText = data.data || "";
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Generation aborted");
      } else {
        showAlertBar("Error: " + err.message, "error");
      }
    } finally {
      isGenerating = false;
      abortController = null;
    }
  }

  function handleUpgrade() {
    showSubscriptionModal = true;
  }

  let referrerCode = "";

  async function handleCheckoutGold() {
    if (!agreedToTos) {
      showAlertBar("You must agree to the Terms & Conditions and Privacy Policy.", "error");
      return;
    }
    if (!devMode && !signedIn) {
      showAlertBar("Please sign in first.", "error");
      return;
    }
    loadingSubscription = true;
    try {
      if (referrerCode.trim()) {
        await fetch(`${API_URL}/referralCheck`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user1Id: user?.uid,
            user2Id: referrerCode.trim()
          })
        })
          .then(async (resp) => {
            if (!resp.ok) {
              const data = await resp.json().catch(() => {});
              throw new Error(data?.message || "Failed to save referral");
            }
          })
          .catch(err => {
            showAlertBar(`Referral code failed: ${err.message}`, "error", 6000);
          });
      }
      const response = await fetch(`${API_URL}/initiateGold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user?.uid }),
      });
      if (!response.ok) {
        throw new Error("Failed to initiate gold upgrade");
      }
      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      showAlertBar("Error initiating upgrade: " + err.message, "error");
    } finally {
      loadingSubscription = false;
    }
  }

  async function handleClaimPlatinum() {
    if (!signedIn) {
      showAlertBar("Please sign in first.", "error");
      return;
    }
    if (referredUsers < 5) {
      showAlertBar("You need at least 5 referred users first.", "error");
      return;
    }
    try {
      const resp = await fetch(`${API_URL}/claimPlatinum`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (!resp.ok) throw new Error("Failed to claim Platinum");
      const data = await resp.json();
      if (data.resultCode === 0) {
        showManageSubscriptionModal = false;
        platinum = true;
        showAlertBar("Congratulations! You are now Platinum!", "success");
      } else {
        showAlertBar("Could not claim Platinum: " + data.message, "error");
      }
    } catch (err) {
      showAlertBar("Error claiming Platinum: " + err.message, "error");
    }
  }

  async function confirmSubscriptionIfNeeded() {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    try {
      const resp = await fetch(`${API_URL}/confirmGold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId })
      });
      if (!resp.ok) throw new Error("Network error confirming subscription.");
      const data = await resp.json();
      if (data.resultCode === 0) {
        showAlertBar("Subscription Upgraded Successfully!", "success");
        gold = true;
      } else {
        showAlertBar("Subscription Confirmation Failed: " + data.message, "error");
      }
    } catch (err) {
      showAlertBar("Error confirming subscription: " + err.message, "error");
    } finally {
      window.history.replaceState({}, document.title, "/");
    }
  }

  // Example records
  let records = [
    {
      title: "Top Gold Referrer",
      profilepic:
        "https://lh3.googleusercontent.com/a/ACg8ocKg2HsSJ0h_25pLmQNTGxIVV5KzT0T3ZvHbV2UHKfBq-5G3ow=s96-c",
      displayName: "Tucker Peters",
      referralCode: "oE6FraOpkJSNtL5vfE7wPimxqms2",
      recordNumber: "Total Referrals: 1"
    },
    {
      title: "First Gold Tier Subscriber",
      profilepic:
        "https://lh3.googleusercontent.com/a/ACg8ocL_HO8bLNFOlQzPiuNBOd6h7p1xD6d_H-Gok6iDvQ8ko9R8jlnL=s96-c",
      displayName: "Matthew Workman",
      referralCode: "cUKD9uDE0eektwmmvvk0gygSz6i2",
      recordNumber: "Subscription Date: January 4th, 2025"
    },
    {
      title: "First Platinum Tier User",
      profilepic: "questionicon.png",
      displayName: "???",
      referralCode: "???",
      recordNumber: "Date Claimed: ???"
    },
    {
      title: "Longest Ongoing Subscription",
      profilepic:
        "https://lh3.googleusercontent.com/a/ACg8ocL_HO8bLNFOlQzPiuNBOd6h7p1xD6d_H-Gok6iDvQ8ko9R8jlnL=s96-c",
      displayName: "Matthew Workman",
      referralCode: "cUKD9uDE0eektwmmvvk0gygSz6i2",
      recordNumber: "Duration: 1 Month"
    },
    {
      title: "Most Valuable Feedback",
      profilepic: "profilepic.png",
      displayName: "Reid Smith",
      referralCode: "orYqWCLAY8ZUYoHLcADr6hBw32k1",
      recordNumber: "Awarded for: Extensive feedback on subscription model"
    },
    {
      title: "Largest Cloaked Submission",
      profilepic:
        "https://lh3.googleusercontent.com/a/ACg8ocKg2HsSJ0h_25pLmQNTGxIVV5KzT0T3ZvHbV2UHKfBq-5G3ow=s96-c",
      displayName: "Tucker Peters",
      referralCode: "oE6FraOpkJSNtL5vfE7wPimxqms2",
      recordNumber: "Characters Cloaked: 25,223"
    },
    {
      title: "Earliest Beta Participant",
      profilepic:
        "https://lh3.googleusercontent.com/a/ACg8ocIa3PXmDF00-4RK8gLj5OK95unwrcU43KRZC8ksb0KP3ct-G9yG=s96-c",
      displayName: "Graham Zemel",
      referralCode: "3t6FzMTVPwMqxATjrOgxDA9Bjfu2",
      recordNumber: "Joined On: December 25th, 2024"
    },
    {
      title: "Secret Achievement",
      profilepic: "questionicon.png",
      displayName: "???",
      referralCode: "???",
      recordNumber: "???"
    }
  ];
</script>

<!-- Navbar -->
<Navbar
  {signedIn}
  {devMode}
  {avatar}
  {userId}
  {userEmail}
  {gold}
  {platinum}
  {admin}
  {referredUsers}
  {showProfileMenu}
  on:signin={handleSignIn}
  on:signout={handleSignOut}
  on:avatarError={handleAvatarError}
  on:toggleProfileMenu={toggleProfileMenu}
  on:upgrade={handleUpgrade}
  on:manageSubscription={() => (showManageSubscriptionModal = true)}
/>

<!-- Tutorial Overlay -->
{#if showTutorial}
  <div
    class="tutorial-background"
    on:click={handleBackgroundClick}
    in:fade={{ duration: 200 }}
    out:fade={{ duration: 200 }}
  >
    {#if highlightBox}
      <div
        class="highlight-spot"
        style="
          top: {highlightBox.top}px;
          left: {highlightBox.left}px;
          width: {highlightBox.width}px;
          height: {highlightBox.height}px;
        "
      ></div>
    {/if}

    <div class="tooltip" style={tooltipStyle} in:fade={{ delay: 200 }} out:fade>
      <h2>{steps[currentStep].title}</h2>
      <p>{steps[currentStep].text}</p>
      <div class="tutorial-buttons">
        {#if currentStep > 0}
          <button on:click={prevStep} class="btn-secondary">Previous</button>
        {/if}
        <button on:click={nextStep} class="btn-primary">
          {currentStep < steps.length - 1 ? "Next" : "Done"}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Bookmark Bar -->
<Bookmark
  show={showBookmarkPrompt}
  on:close={() => (showBookmarkPrompt = false)}
/>

{#if showHowToUse}
  <HowToUseOverlay on:close={toggleHowToUse} />
{/if}

{#if alertVisible}
  <AlertBar {alertMessage} {alertType} />
{/if}

<!-- Main App Container -->
<div class="container">
  <h1>
    {#if admin}
      AI Cloaker <span class="admin-shimmer">Admin</span>
    {:else if platinum}
      AI Cloaker <span class="platinum-shimmer">Platinum</span>
    {:else if gold}
      AI Cloaker <span class="gold-shimmer">Gold</span>
    {:else}
      AI Cloaker
    {/if}
  </h1>

  {#if userId === "cUKD9uDE0eektwmmvvk0gygSz6i2"}
    <p class="special-subscriber-banner">
      <Fa icon={faStar} /> 1st Gold Subscriber!
    </p>
  {/if}

  <p class="subtitle">
    Our AI cloaking algorithm makes AI-generated text undetectable to both
    <strong>human readers</strong> and <strong>AI detectors</strong>.
  </p>

  <div class="main-content">
    <div class="input-panel">
      <div class="control-row">
        <label for="mode-select" class="dropdown-label">Cloak Style:</label>
        <select id="mode-select" bind:value={mode} class="mode-select">
          <option value="generic">Generic</option>
          <option value="professional">Professional</option>
          <option value="casual">Casual</option>
          <option value="academic">Academic</option>
          <option value="creative">Creative</option>
          <option value="my_style">My Style</option>
        </select>
      </div>

      <textarea
        class="text-area"
        placeholder="Type or paste your text here..."
        bind:value={inputText}
      />
      <div class="wordchar-count">
        <span>Words: {wordCount}</span>
        <span> | </span>
        <span>Characters: {charCount}</span>
      </div>

      {#if mode === "my_style"}
        <div class="style-upload">
          <input
            id="upload-file"
            type="file"
            accept=".pdf, .txt"
            class="file-input-hidden"
            on:change={uploadStyle}
          />
          <div class="file-upload-row">
            <label for="upload-file" class="btn btn-secondary file-upload-label">
              Choose File
            </label>
            {#if styleFilename}
              <p class="file-status">Uploaded: {styleFilename}</p>
            {:else}
              <p class="file-status">Please upload a file here!</p>
            {/if}
          </div>
        </div>
      {/if}

      <div class="button-row">
        <button class="how-to-btn btn btn-secondary" on:click={toggleHowToUse}>
          <Fa icon={faCircleQuestion} />
          How to Use
        </button>

        <button
          class="btn btn-primary"
          id="rewrite-button"
          on:click={humanizeText}
          disabled={isGenerating}
        >
          {#if isGenerating}
            <Fa icon={faSpinner} spin /> Rewriting...
          {:else}
            Rewrite
          {/if}
        </button>
      </div>
    </div>

    <div class="output-panel">
      <div class="output-header">
        <span>Output Text</span>
        <button class="btn btn-secondary" on:click={copyOutput}>
          <Fa icon={faCopy} /> Copy
        </button>
      </div>
      <textarea
        readonly
        class="text-area output-textarea"
        placeholder="Your rewritten text will appear here..."
        bind:value={outputText}
      />
    </div>
  </div>

  <br />
  {#if userId}
    <p class="usage-disclaimer">Referral Code: {userId}</p>
  {/if}
  <p class="usage-disclaimer">
    By using AI Cloaker, you agree to the
    <a href="https://aicloaker.com/privacypolicy.html" target="_blank">
      privacy policy
    </a>
    and
    <a href="https://aicloaker.com/termsofservice.html" target="_blank">
      terms of service
    </a>.
  </p>
</div>

<!-- Subscription Modal -->
{#if showSubscriptionModal}
  <SubscriptionModal
    {referrerCode}
    {agreedToTos}
    {loadingSubscription}
    {userId}
    {referredUsers}
    {signedIn}
    {devMode}
    on:close={() => (showSubscriptionModal = false)}
    on:updateAgreed={(e) => (agreedToTos = e.detail)}
    on:checkoutGold={handleCheckoutGold}
    on:claimPlatinum={handleClaimPlatinum}
  />
{/if}

<!-- Manage Subscription Modal -->
{#if showManageSubscriptionModal}
  <ManageSubscriptionModal
    {gold}
    {referredUsers}
    {signedIn}
    {devMode}
    on:close={() => (showManageSubscriptionModal = false)}
    on:cancelGold={handleCancelGold}
    on:claimPlatinum={handleClaimPlatinum}
  />
{/if}
<!-- Admin Only -->
{#if admin}
  <section class="admin-section">
    <h2>Admin Controls</h2>
    <label style="cursor: pointer;">
      <input
        type="checkbox"
        checked={underDevelopment}
        on:change={(e) => updateGlobalSettings(e.target.checked)}
      />
      <span>Under Development</span>
    </label>
  </section>
{/if}

<h2 style="text-align: center;">AI Cloaker Records</h2>
<Records {records} />


<!-- If the site is under development, show overlay for non-admins -->
{#if !admin && underDevelopment}
  <div class="dev-overlay">
    <div class="dev-overlay-content">
      <h2>AI Cloaker is under development at this time.</h2>
      <p>Please come back later.</p>
    </div>
  </div>
{/if}

<Footer />

<style>
  :global(html, body) {
    margin: 0;
    padding: 0;
    height: 100%;
  }
  :global(body) {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: #0a0b0e;
    color: #e2e8f0;
    font-family: sans-serif;
  }
  .container {
    flex: 1;
    max-width: 1800px;
    margin: 2rem 12rem;
    padding: 2rem 6rem;
    background: #13151a;
    border: 1px solid #424867;
    border-radius: 6px;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
    animation: fadeIn 0.5s ease-out;
  }
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  h1 {
    text-align: center;
    margin-bottom: 0.5rem;
    font-size: 2.5rem;
    letter-spacing: -0.5px;
    background: linear-gradient(120deg, #646cff, #ff6b6b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .subtitle {
    text-align: center;
    color: #94a3b8;
    margin-bottom: 3rem;
    font-size: 1.1rem;
  }
  .special-subscriber-banner {
    text-align: center;
    font-size: 1.1rem;
    margin-bottom: 1rem;
    color: #ffd700;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }
  .main-content {
    display: grid;
    gap: 2rem;
    grid-template-columns: 1fr;
    align-items: start;
  }
  @media (min-width: 768px) {
    .main-content {
      grid-template-columns: 1fr 1fr;
    }
  }
  .input-panel,
  .output-panel {
    display: flex;
    flex-direction: column;
  }
  .control-row {
    margin-bottom: 1.5rem;
    display: flex;
    gap: 1rem;
    align-items: center;
  }
  .dropdown-label {
    color: #e2e8f0;
    font-size: 1rem;
    margin-right: 0.25rem;
  }
  .mode-select {
    width: 220px;
    padding: 0.75rem;
    border: 1px solid #424867;
    border-radius: 8px;
    background-color: #0a0b0e;
    background-image: linear-gradient(45deg, transparent 50%, #646cff 50%),
      linear-gradient(135deg, #646cff 50%, transparent 50%);
    background-position: calc(100% - 20px) calc(1.1em),
      calc(100% - 15px) calc(1.1em);
    background-size: 5px 5px;
    background-repeat: no-repeat;
    color: #e2e8f0;
    font-size: 1rem;
    appearance: none;
    cursor: pointer;
    transition: 0.3s ease;
  }
  .mode-select:hover {
    border-color: #646cff;
  }
  .mode-select:focus {
    outline: none;
    border-color: #646cff;
    box-shadow: 0 0 0 3px rgba(100,108,255,0.2);
  }
  .text-area {
    width: 100%;
    min-height: 250px;
    padding: 1rem;
    border: 1px solid #424867;
    border-radius: 8px;
    background-color: #0a0b0e;
    color: #e2e8f0;
    resize: vertical;
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 0.25rem;
  }
  .text-area:focus {
    outline: none;
    border-color: #646cff;
    box-shadow: 0 0 0 3px rgba(100,108,255,0.2);
  }
  .wordchar-count {
    font-size: 0.85rem;
    color: #aaa;
    margin-bottom: 1rem;
  }
  .style-upload {
    margin-bottom: 1.5rem;
    padding: 2rem;
    border: 1px solid #424867;
    border-radius: 12px;
    background: linear-gradient(145deg, rgba(19,21,26,0.95), rgba(25,28,35,0.95));
    box-shadow: 0 8px 32px rgba(0,0,0,0.2);
    backdrop-filter: blur(8px);
    transition: 0.3s ease;
  }
  .file-upload-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
  }
  .file-input-hidden {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
  .file-upload-label {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    color: #e2e8f0;
    background-color: #424867;
    transition: 0.3s ease;
  }
  .file-upload-label:hover {
    background: #646cff;
  }
  .file-status {
    font-size: 0.9rem;
    color: #bbb;
    margin: 0;
  }
  .button-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
  .btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    transition: 0.3s ease;
    color: #e2e8f0;
  }
  .btn-primary {
    background: linear-gradient(135deg, #646cff, #818cf8);
  }
  .btn-secondary {
    background-color: #424867;
  }
  .btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .btn:hover:not(:disabled) {
    box-shadow: 0 0 15px rgba(100,108,255,0.3);
  }
  .output-panel {
    background-color: #1a1c22;
    padding: 1rem 1rem 1.5rem;
    border-radius: 8px;
    border: 1px solid #424867;
    box-shadow: 0 3px 20px rgba(0,0,0,0.3);
  }
  .output-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    font-weight: 600;
    color: #e2e8f0;
  }
  .output-textarea {
    background-color: #14161b;
    box-shadow: inset 0 0 10px rgba(100,108,255,0.2);
  }
  .usage-disclaimer {
    margin-top: 1rem;
    text-align: center;
    font-size: 0.9rem;
    color: #ccc;
    line-height: 1.4;
  }
  .usage-disclaimer a {
    color: #646cff;
    text-decoration: none;
  }
  .usage-disclaimer a:hover {
    text-decoration: underline;
  }
  .gold-shimmer {
    background: linear-gradient(
      90deg,
      #ffd700,
      #ffc300,
      #ff8c00,
      #ffc300,
      #ffd700
    );
    background-size: 300% auto;
    animation: shine 4s linear infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
  .admin-shimmer {
    background: linear-gradient(
      90deg,
      #ff3b3b,
      #ff6666,
      #e60000,
      #ff6666,
      #ff3b3b
    );
    background-size: 300% auto;
    animation: shine 4s linear infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
  .platinum-shimmer {
    background: linear-gradient(
      90deg,
      #c0c0c0,
      #d3d3d3,
      #a8a8a8,
      #d3d3d3,
      #c0c0c0
    );
    background-size: 300% auto;
    animation: shine 4s linear infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
  @keyframes shine {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  .tutorial-background {
    position: fixed;
    top: 0; 
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    z-index: 99998;
  }
  .highlight-spot {
    position: absolute;
    border: 2px solid #fff;
    border-radius: 6px;
    pointer-events: none;
    box-shadow: 0 0 0 99999px rgba(0,0,0,0.4);
    z-index: 100000;
  }
  .tooltip {
    position: absolute;
    min-width: 250px;
    max-width: 320px;
    background: #13151a;
    border: 1px solid #424867;
    border-radius: 8px;
    box-shadow: 0 0 10px rgba(0,0,0,0.5);
    padding: 1rem;
    color: #e2e8f0;
    transform: translate(-50%, 0);
    z-index: 100000;
  }
  .tooltip h2 {
    margin: 0 0 0.5rem;
    font-size: 1.2rem;
    background: linear-gradient(120deg, #646cff, #ff6b6b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .tooltip p {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    line-height: 1.3rem;
  }
  .tutorial-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }
  .btn-primary,
  .btn-secondary {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    color: #e2e8f0;
    transition: 0.3s ease;
  }
  .btn-primary {
    background: linear-gradient(135deg, #646cff, #818cf8);
  }
  .btn-secondary {
    background-color: #424867;
  }
  .btn-primary:hover,
  .btn-secondary:hover {
    box-shadow: 0 0 10px rgba(100,108,255,0.3);
  }
  .admin-section {
    text-align: center;
    margin: 2rem 0;
    border-top: 1px solid #424867;
    padding-top: 2rem;
  }
  .dev-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.9);
    color: #fff;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem;
  }
  .dev-overlay-content {
    max-width: 80%;
    background: #333;
    border-radius: 10px;
    padding: 2rem;
  }
</style>
